import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { trpc } from '../trpc/client';
import { useStore, useTranslation } from '../store/useStore';
import {
  Search, SlidersHorizontal, Star, TrendingUp, Award, Zap,
  ChevronDown, X, Grid3X3, List, MapPin, ArrowRight
} from 'lucide-react';

export default function Deals() {
  const { t, language } = useTranslation();
  const { currency } = useStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState<number | undefined>(
    searchParams.get('category') ? Number(searchParams.get('category')) : undefined
  );
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [dealType, setDealType] = useState<string>('');
  const [sortBy, setSortBy] = useState('featured');
  const [showFilters, setShowFilters] = useState(false);

  const { data: categoriesData } = trpc.util.getCategories.useQuery({});
  const { data: dealsData, isLoading } = trpc.deal.list.useQuery({
    categoryId: selectedCategory,
    search: searchQuery || undefined,
    minPrice: minPrice ? Number(minPrice) : undefined,
    maxPrice: maxPrice ? Number(maxPrice) : undefined,
    dealType: dealType as 'product' | 'service' | undefined,
    sortBy: sortBy || undefined,
    page: 1,
    limit: 24,
  });

  const categories = categoriesData || [];
  const deals = dealsData?.deals || [];
  const total = dealsData?.total || 0;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
  };

  const clearFilters = () => {
    setSelectedCategory(undefined);
    setMinPrice('');
    setMaxPrice('');
    setDealType('');
    setSearchQuery('');
  };

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#f8fafc]">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-semibold text-[#1e293b] mb-2 font-display">Browse Deals</h1>
          <p className="text-[#64748b]">{total.toLocaleString()} deals available worldwide</p>
        </div>

        {/* Search + Filters Bar */}
        <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-6 shadow-sm">
          <div className="flex flex-col md:flex-row gap-3">
            <form onSubmit={handleSearch} className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748b]" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('search.placeholder')}
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-[#c2410c] focus:ring-2 focus:ring-[#c2410c]/20 outline-none text-sm"
              />
            </form>
            <div className="flex gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition-colors ${showFilters ? 'border-[#c2410c] text-[#c2410c] bg-[#c2410c]/5' : 'border-gray-200 text-[#1e293b] hover:border-[#c2410c]'}`}
              >
                <SlidersHorizontal className="w-4 h-4" /> Filters
              </button>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-3 rounded-xl border border-gray-200 text-sm focus:border-[#c2410c] outline-none bg-white"
              >
                <option value="featured">Featured</option>
                <option value="price_asc">Price: Low to High</option>
                <option value="price_desc">Price: High to Low</option>
                <option value="newest">Newest</option>
                <option value="rating">Highest Rated</option>
              </select>
            </div>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wider mb-2 block">Category</label>
                <select
                  value={selectedCategory || ''}
                  onChange={(e) => setSelectedCategory(e.target.value ? Number(e.target.value) : undefined)}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-[#c2410c] outline-none"
                >
                  <option value="">All Categories</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wider mb-2 block">Deal Type</label>
                <select
                  value={dealType}
                  onChange={(e) => setDealType(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-[#c2410c] outline-none"
                >
                  <option value="">All Types</option>
                  <option value="product">Product</option>
                  <option value="service">Service</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wider mb-2 block">Min Price</label>
                <input
                  type="number"
                  value={minPrice}
                  onChange={(e) => setMinPrice(e.target.value)}
                  placeholder="0"
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-[#c2410c] outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wider mb-2 block">Max Price</label>
                <input
                  type="number"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(e.target.value)}
                  placeholder="No limit"
                  className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-[#c2410c] outline-none"
                />
              </div>
              <div className="sm:col-span-2 lg:col-span-4 flex justify-end">
                <button onClick={clearFilters} className="text-sm text-[#c2410c] hover:text-[#9a3412] font-medium flex items-center gap-1">
                  <X className="w-4 h-4" /> Clear all filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Active Filters */}
        {(selectedCategory || minPrice || maxPrice || dealType || searchQuery) && (
          <div className="flex flex-wrap gap-2 mb-6">
            {selectedCategory && (
              <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#c2410c]/10 text-[#c2410c] text-xs font-medium rounded-full">
                {categories.find(c => c.id === selectedCategory)?.name}
                <button onClick={() => setSelectedCategory(undefined)}><X className="w-3 h-3" /></button>
              </span>
            )}
            {dealType && (
              <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#c2410c]/10 text-[#c2410c] text-xs font-medium rounded-full capitalize">
                {dealType}
                <button onClick={() => setDealType('')}><X className="w-3 h-3" /></button>
              </span>
            )}
            {searchQuery && (
              <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#c2410c]/10 text-[#c2410c] text-xs font-medium rounded-full">
                Search: {searchQuery}
                <button onClick={() => setSearchQuery('')}><X className="w-3 h-3" /></button>
              </span>
            )}
          </div>
        )}

        {/* Results */}
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden border border-gray-100 animate-pulse">
                <div className="h-48 bg-gray-200" />
                <div className="p-4 space-y-3">
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-3 bg-gray-200 rounded w-full" />
                  <div className="h-6 bg-gray-200 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : deals.length === 0 ? (
          <div className="text-center py-20">
            <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-[#1e293b] mb-2">No deals found</h3>
            <p className="text-[#64748b] mb-4">Try adjusting your filters or search terms</p>
            <button onClick={clearFilters} className="text-[#c2410c] font-medium hover:underline">Clear all filters</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {deals.map((deal) => (
              <DealCard key={deal.id} deal={deal} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DealCard({ deal }: { deal: any }) {
  const { currency } = useStore();
  const mainImage = deal.images?.[0] || '';

  return (
    <Link to={`/deals/${deal.slug}`} className="group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-xl transition-all hover:-translate-y-1.5 flex flex-col">
      <div className="relative h-44 overflow-hidden shrink-0">
        <img
          src={mainImage}
          alt={deal.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        {deal.featured && (
          <div className="absolute top-2 right-2 px-2 py-0.5 bg-[#c2410c] text-white text-[10px] font-bold uppercase tracking-wider rounded-md flex items-center gap-1">
            <TrendingUp className="w-3 h-3" /> Featured
          </div>
        )}
        {deal.dealType === 'service' && (
          <div className="absolute top-2 left-2 px-2 py-0.5 bg-white/90 backdrop-blur-sm text-[#1e293b] text-[10px] font-bold uppercase tracking-wider rounded-md">
            Service
          </div>
        )}
      </div>
      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-[10px] font-semibold text-[#c2410c] uppercase tracking-wider">{deal.category?.name}</span>
          <span className="text-[#e2e8f0]">|</span>
          <span className="text-[10px] text-[#64748b] flex items-center gap-0.5">
            <Award className="w-3 h-3" /> Verified
          </span>
        </div>
        <h3 className="text-sm font-semibold text-[#1e293b] mb-2 line-clamp-2 group-hover:text-[#c2410c] transition-colors flex-1">{deal.name}</h3>
        <div className="flex items-end justify-between mt-auto">
          <div>
            <p className="text-[10px] text-[#94a3b8] mb-0.5">IPO Price</p>
            <p className="text-xl font-bold text-[#1e293b] font-display">{currency === 'USD' ? '$' : '€'}{Number(deal.buyerPrice).toLocaleString()}</p>
          </div>
          <div className="flex items-center gap-1 text-amber-500">
            <Star className="w-3.5 h-3.5 fill-current" />
            <span className="text-xs font-semibold">{deal.avgRating}</span>
          </div>
        </div>
        {deal.groupBuyingEnabled && (
          <div className="mt-2 px-2 py-1 bg-emerald-50 text-emerald-700 text-[10px] font-semibold rounded-md text-center">
            Group Buying Available
          </div>
        )}
      </div>
    </Link>
  );
}
